/* globals gtag*/
import React, { useCallback, useState, useMemo, useRef, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  SetPage,
  SetUrlPrefix,
  SetImgPrefix,
  SetProducts,
  SetIsSearching,
  SetPrevProducts,
  SetLastUpdate,
  SetAsyncData,
} from "../../state/actions/index"
import "./ResultsContainer.css"
import PerfectScrollbar from "react-perfect-scrollbar"
import CategoriesContainer from "../CategoriesContainer/CategoriesContainer"
import ProductsContainer from "../ProductsContainer/ProductsContainer"
import Paginator from "../Paginator/Paginator"
import Utils from "../../utils/Utils"
import axios from "axios"

export function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false)
  const observer = useMemo(
    () =>
      !!ref &&
      new IntersectionObserver(([entry]) =>
        setIntersecting(entry.isIntersecting)
      ),
    [ref]
  )

  useEffect(() => {
    if (
      !!ref &&
      observer instanceof IntersectionObserver &&
      ref.current instanceof Element
    ) {
      observer.observe(ref.current)
      return () => observer?.disconnect()
    }
  }, [ref, observer])

  return isIntersecting
}

const ResultsContainer = (props) => {
  const triggers = useSelector((state) => state.config.triggers)
  const card = useSelector((state) => state.config.card)
  const shopId = useSelector((state) => state.config.shopId)
  const baseUrl = useSelector((state) => state.config.baseUrl)
  const configFilters = useSelector((state) => state.config.filters)
  const activeFilters = useSelector((state) => state.activeFilters)
  const searchWord = useSelector((state) => state.searchWord)
  const lastUpdate = useSelector((state) => state.lastUpdate)
  const products = useSelector((state) => state.products)
  const isMobile = useSelector((state) => state.isMobile)
  const showFilters = useSelector((state) => state.showFilters)
  const listingBanner = useSelector((state) => state.listingBanner)
  const ga4 = useSelector((state) => state.config.ga4)
  // const promotionsListing = useSelector(state => state.config.promotions.listing);
  const sort = useSelector((state) => state.sort)
  const page = useSelector((state) => state.page)
  const pages = useSelector((state) => state.pages)
  const autocomplete = useSelector((state) => state.config.autocomplete)
  const display = useSelector((state) => state.config.display)
  const config = useSelector((state) => state.config)
  // const isSearching = useSelector(state => state.isSearching);
  const resultsCount = useSelector((state) => state.resultsCount)
  const nameProducts = useSelector(
    (state) => state.config.placeholders.products
  )
  const { setSubCategory } = props
  var minChars = useMemo(
    () =>
      isMobile ? autocomplete.minChars.mobile : autocomplete.minChars.desktop,
    [isMobile, autocomplete]
  )
  var isSearchwordShort = useMemo(
    () => searchWord.length < minChars,
    [searchWord, minChars]
  )
  const [isRendering, setIsRendering] = useState(false)
  const dispatch = useDispatch()
  // var reactivePromotionsListing=useMemo(()=>isMobile?promotionsListing.mobile:(promotionsListing.desktop),[isMobile,promotionsListing]);
  var reactivePromotionsListing = useMemo(
    () => (isMobile ? listingBanner?.mobileImageUrl : listingBanner?.imageUrl),
    [isMobile, listingBanner]
  )
  const queryOptions = useSelector((state) => state.config.queryOptions)
  var rows = useMemo(
    () => (isMobile ? queryOptions.rows.mobile : queryOptions.rows.desktop),
    [isMobile, queryOptions]
  )
  var infiniteScrolling = useMemo(
    () =>
      isMobile
        ? triggers.infiniteScrolling.mobile === "infinite-scrolling"
        : triggers.infiniteScrolling.desktop === "infinite-scrolling",
    [isMobile, triggers]
  )
  const ps = useRef(null)

  const promoRef = useRef(null)
  const isPromViewed = useOnScreen(promoRef)
  const [promoViewed, setPromoViewed] = useState(false)
  useEffect(() => {
    if (
      isPromViewed &&
      !promoViewed &&
      ((!!listingBanner?.imageUrl && !triggers.dynamicBanner) ||
        !!triggers.dynamicBanner)
    ) {
      setPromoViewed(true)
      Utils.sendAnalyticsObject(baseUrl, shopId, "as_view_promotion", {
        id: listingBanner?.id ?? 0,
        name: listingBanner?.name ?? "",
        type: "listing",
        device: isMobile ? "mobile" : "desktop",
      })
      if (!!ga4?.isActive) {
        if (typeof window.dataLayer !== "undefined" && !!window.dataLayer) {
          window.dataLayer.push({
            event: "as_view_promotion",
            data: {
              id: listingBanner?.id ?? 0,
              name: listingBanner?.name ?? "",
              type: "listing",
              device: isMobile ? "mobile" : "desktop",
            },
          })
        } else if (typeof gtag !== "undefined") {
          gtag("event", "as_view_promotion", {
            id: listingBanner?.id ?? 0,
            name: listingBanner?.name ?? "",
            type: "listing",
            device: isMobile ? "mobile" : "desktop",
          })
        }
      }
    }
  }, [
    baseUrl,
    ga4,
    isMobile,
    isPromViewed,
    listingBanner,
    promoViewed,
    shopId,
    triggers.dynamicBanner,
  ])

  const setPage = useCallback(() => {
    if (
      infiniteScrolling &&
      !isRendering &&
      pages > page &&
      products.length > 0 &&
      ps.current._ps.scrollbarYTop > 0
    ) {
      var pageToUse = page + 1
      setIsRendering(true)
      dispatch(SetIsSearching(true))
      // dispatch(SetAsyncData([]));
      var priceGroup = {}
      priceGroup.id =
        window.AS_ADVANCED_CONFIG && window.AS_ADVANCED_CONFIG.visitorType
          ? window.AS_ADVANCED_CONFIG.visitorType
          : config.priceGroup.id
      priceGroup.currency =
        window.AS_ADVANCED_CONFIG && window.AS_ADVANCED_CONFIG.currencyCode
          ? window.AS_ADVANCED_CONFIG.currencyCode
          : config.priceGroup.currency
      var localSearchUrl = Utils.getSearchUrl(
        config.baseUrl,
        config.queryOptions,
        priceGroup,
        config.shopId,
        config.jsonFast
      )
      axios
        .get(
          Utils.insertWordInSearchUrl(
            localSearchUrl,
            searchWord,
            activeFilters,
            sort,
            pageToUse,
            configFilters,
            rows
          )
        )
        .then((response) => {
          if (
            (response.extraData && !response.extraData.originated_at) ||
            lastUpdate <= response.data.extraData.originated_at
          ) {
            dispatch(SetLastUpdate(response.data.extraData.originated_at))
            dispatch(SetPage(pageToUse))
            dispatch(SetPrevProducts(products))
            dispatch(
              SetProducts(
                Utils.refactorUrl(
                  [...products, ...response.data.data],
                  response.data.header.imagePrefix,
                  response.data.header.urlPrefix,
                  card.img,
                  card.url
                )
              )
            )
            dispatch(SetUrlPrefix(response.data.header.urlPrefix))
            dispatch(SetImgPrefix(response.data.header.imagePrefix))
            if (config.webhook.key !== "")
              Utils.getAsyncData(
                config.webhook,
                [...products, ...response.data.data],
                priceGroup
              ).then((value) => dispatch(SetAsyncData(value.asyncData)))
            var websiteSearchBar = document.getElementById(
              "accelasearch-bar-container-container"
            )
            const event = new CustomEvent("as-search-event", {
              detail: {
                searchWord: searchWord,
                filters: activeFilters,
                page: page,
              },
            })
            websiteSearchBar.dispatchEvent(event)
          }
        })
        .finally(() => {
          setIsRendering(false)
          dispatch(SetIsSearching(false))
        })
    }
  }, [
    dispatch,
    activeFilters,
    searchWord,
    sort,
    config,
    page,
    products,
    setIsRendering,
    configFilters,
    lastUpdate,
    isRendering,
    infiniteScrolling,
    pages,
    rows,
    card,
  ])
  function handlePromotionClick() {
    sessionStorage.setItem("isLayerOpen", "false")
    Utils.sendAnalyticsObject(baseUrl, shopId, "as_click_promotion", {
      id: listingBanner?.id ?? 0,
      name: listingBanner?.name ?? "",
      type: "listing",
      device: isMobile ? "mobile" : "desktop",
    })
    if (!!ga4?.isActive) {
      if (typeof window.dataLayer !== "undefined" && !!window.dataLayer) {
        window.dataLayer.push({
          event: "as_click_promotion",
          data: {
            id: listingBanner?.id ?? 0,
            name: listingBanner?.name ?? "",
            type: "listing",
            device: isMobile ? "mobile" : "desktop",
          },
        })
      } else if (typeof gtag !== "undefined") {
        gtag("event", "as_click_promotion", {
          id: listingBanner?.id ?? 0,
          name: listingBanner?.name ?? "",
          type: "listing",
          device: isMobile ? "mobile" : "desktop",
        })
      }
    }
  }

  return (
    <div
      className={"results-container" + (showFilters ? " show-filters" : "")}
      ref={promoRef}
    >
      {display === "half-screen" &&
      (searchWord !== "" || activeFilters.length > 0) /* && !isSearching */ ? (
        <div className="results-count">
          {" "}
          {resultsCount + " " + nameProducts}{" "}
        </div>
      ) : null}
      <PerfectScrollbar ref={ps} onYReachEnd={setPage}>
        {(activeFilters.length > 0 || !isSearchwordShort) &&
        !!reactivePromotionsListing &&
        !triggers.dynamicBanner ? (
          <div className="listing promo">
            <a
              href={listingBanner.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handlePromotionClick()}
            >
              <img
                src={reactivePromotionsListing}
                alt={reactivePromotionsListing}
              />
            </a>
          </div>
        ) : triggers.dynamicBanner ? (
          <div
            id={
              isMobile
                ? "accelasearch-dynamic-banner-mobile"
                : "accelasearch-dynamic-banner"
            }
            onClick={() => handlePromotionClick()}
          ></div>
        ) : null}
        {triggers.icebreaker.isActive && activeFilters.length === 0 ? (
          <CategoriesContainer setSubCategory={setSubCategory} />
        ) : null}
        <ProductsContainer />
        {infiniteScrolling ? null : <Paginator />}
      </PerfectScrollbar>
    </div>
  )
}

export default ResultsContainer
